import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, ShoppingBag, Lock } from "lucide-react";
import { useCart } from "../../context/CartContext";

const money = (amount, currency) => {
  if (amount == null) return "";
  const symbols = { USD: "$", GBP: "£", EUR: "€" };
  const sym = symbols[currency] || "";
  return `${sym}${Number(amount).toFixed(2)}${sym ? "" : " " + (currency || "")}`;
};

export const CartDrawer = () => {
  const { cart, open, setOpen, updateLine, loading } = useCart();
  const lines = cart?.lines || [];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] bg-matte/70 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[81] h-full w-full max-w-md bg-matte border-l border-silver/15 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-silver/10">
              <h3 className="heading text-3xl text-cream flex items-center gap-3">
                <ShoppingBag size={20} className="text-stone" /> Your Bag
              </h3>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-silver/60 hover:text-cream">
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {lines.length === 0 ? (
                <p className="text-silver/50 text-center mt-20">Your bag is empty.</p>
              ) : (
                <div className="space-y-5">
                  {lines.map((l) => (
                    <div key={l.line_id} data-testid={`cart-line-${l.line_id}`} className="flex gap-4">
                      {l.image && <img src={l.image} alt={l.title} className="w-20 h-24 object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-cream text-sm">{l.title}</p>
                        {l.variant_title && l.variant_title !== "Default Title" && (
                          <p className="text-silver/50 text-xs">{l.variant_title}</p>
                        )}
                        <p className="text-stone text-sm mt-1">{money(l.price, l.currency)}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            data-testid={`cart-dec-${l.line_id}`}
                            onClick={() => updateLine(l.line_id, l.quantity - 1)}
                            className="w-7 h-7 border border-silver/25 flex items-center justify-center text-cream hover:border-stone"
                          >
                            <Minus size={13} />
                          </button>
                          <span className="text-cream text-sm w-5 text-center">{l.quantity}</span>
                          <button
                            data-testid={`cart-inc-${l.line_id}`}
                            onClick={() => updateLine(l.line_id, l.quantity + 1)}
                            className="w-7 h-7 border border-silver/25 flex items-center justify-center text-cream hover:border-stone"
                          >
                            <Plus size={13} />
                          </button>
                          <button
                            data-testid={`cart-remove-${l.line_id}`}
                            onClick={() => updateLine(l.line_id, 0)}
                            className="ml-auto text-silver/50 hover:text-red-400 text-xs uppercase tracking-[0.15em]"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {lines.length > 0 && (
              <div className="border-t border-silver/10 px-6 py-6 space-y-4">
                <div className="flex items-center justify-between text-cream">
                  <span className="uppercase tracking-[0.2em] text-xs text-silver/60">Subtotal</span>
                  <span className="heading text-2xl">{money(cart?.subtotal, cart?.currency)}</span>
                </div>
                <a
                  data-testid="cart-checkout"
                  href={cart?.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`btn-primary w-full ${!cart?.checkout_url ? "pointer-events-none opacity-50" : ""}`}
                >
                  <Lock size={14} /> {loading ? "…" : "Secure Checkout"}
                </a>
                <p className="text-silver/40 text-[0.65rem] text-center">
                  Checkout is powered by Shopify's secure payment page.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
